import React, { useState } from 'react';
import firebaseAuthService from '../services/firebaseAuthService';
import { useTranslation } from '../services/translationService';
import { Field, Icon } from './ui';
import { AuthLayout, PasswordInput, authErrorText } from './AuthLayout';

const FirebaseFarmRegistration = ({ onRegistrationSuccess, onBackToLogin, authService = firebaseAuthService }) => {
  const { language } = useTranslation();
  const ro = language === 'ro';
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [form, setForm] = useState({
    invitationCode: '', farmName: '', managerName: '', managerEmail: '', managerPassword: '', confirmPassword: '', location: '', acceptTerms: false,
  });
  const set = (key) => (event) => setForm({ ...form, [key]: event.target.type === 'checkbox' ? event.target.checked : event.target.value });
  const backLink = (
    <p className="au-foot">
      {ro ? 'Ai deja cont? ' : 'Already have an account? '}
      <button type="button" className="au-link" disabled={loading} onClick={onBackToLogin}>{ro ? 'Conectează-te' : 'Sign in'}</button>
    </p>
  );
  const title = ro ? 'Înregistrează ferma' : 'Register your farm';

  const checkCode = async (event) => {
    event.preventDefault();
    setError('');
    if (!form.invitationCode.trim()) {
      setError(ro ? 'Scrie codul de invitație.' : 'Please enter an invitation code.');
      return;
    }
    setLoading(true);
    try {
      if (await authService.validateInvitationCode(form.invitationCode.trim())) setStep(2);
      else setError(ro ? 'Codul de invitație nu este valid sau a expirat.' : 'That invitation code is invalid or has expired.');
    } catch (err) {
      setError(authErrorText(err, ro));
    } finally {
      setLoading(false);
    }
  };

  const problem = () => {
    if (!form.farmName.trim() || !form.managerName.trim() || !form.managerEmail.trim()) return ro ? 'Completează numele fermei, numele tău și emailul.' : 'Please fill in the farm name, your name and your email.';
    if (form.managerPassword.length < 6) return ro ? 'Parola trebuie să aibă cel puțin 6 caractere.' : 'The password must have at least 6 characters.';
    if (form.managerPassword !== form.confirmPassword) return ro ? 'Parolele nu sunt la fel.' : 'The passwords do not match.';
    if (!form.acceptTerms) return ro ? 'Trebuie să accepți termenii și condițiile.' : 'Please accept the terms and conditions.';
    return '';
  };

  const register = async (event) => {
    event.preventDefault();
    const found = problem();
    setError(found);
    if (found) return;
    setLoading(true);
    try {
      const { user, farm } = await authService.registerFarm(
        { name: form.farmName.trim(), location: form.location.trim() },
        { name: form.managerName.trim(), email: form.managerEmail.trim(), password: form.managerPassword },
        form.invitationCode.trim(),
      );
      setStep(3);
      setTimeout(() => onRegistrationSuccess({ user, farm }), 2000);
    } catch (err) {
      setError(authErrorText(err, ro));
    } finally {
      setLoading(false);
    }
  };

  if (step === 3) {
    return (
      <AuthLayout title={title}>
        <div className="au-done" role="status">
          <span><Icon d="M5 13l4 4L19 7" size={30} /></span>
          <h2>{ro ? 'Ferma este gata' : 'Your farm is ready'}</h2>
          <p className="au-sub">{ro ? 'Te ducem în aplicație. Vei putea crea conturi pentru angajați din Setări.' : 'Taking you to the app. You can create employee accounts from Settings.'}</p>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout title={title} text={ro ? 'Ai nevoie de un cod de invitație de la administrator.' : 'You need an invitation code from your administrator.'}>
      <div className="au-steps" aria-hidden="true"><i className="is-on" /><i className={step === 2 ? 'is-on' : ''} /></div>
      <div>
        <h2>{step === 1 ? (ro ? 'Codul de invitație' : 'Invitation code') : (ro ? 'Ferma și contul tău' : 'Your farm and account')}</h2>
        <p className="au-sub">{ro ? `Pasul ${step} din 2` : `Step ${step} of 2`}</p>
      </div>
      {error && <p className="pm-error" role="alert">{error}</p>}

      {step === 1 && (
        <form className="au-form" onSubmit={checkCode} noValidate>
          <Field label={ro ? 'Cod de invitație' : 'Invitation code'}>
            <input autoCapitalize="none" autoComplete="off" value={form.invitationCode} disabled={loading} onChange={set('invitationCode')} />
          </Field>
          <button type="submit" className="pm-btn-primary" disabled={loading || !form.invitationCode.trim()}>{loading ? (ro ? 'Se verifică…' : 'Checking…') : (ro ? 'Continuă' : 'Continue')}</button>
        </form>
      )}

      {step === 2 && (
        <form className="au-form" onSubmit={register} noValidate>
          <Field label={ro ? 'Numele fermei' : 'Farm name'}><input autoComplete="organization" value={form.farmName} disabled={loading} onChange={set('farmName')} /></Field>
          <Field label={ro ? 'Numele tău' : 'Your name'}><input autoComplete="name" value={form.managerName} disabled={loading} onChange={set('managerName')} /></Field>
          <Field label="Email"><input type="email" inputMode="email" autoCapitalize="none" autoComplete="username" value={form.managerEmail} disabled={loading} onChange={set('managerEmail')} /></Field>
          <Field label={ro ? 'Parolă' : 'Password'} hint={ro ? 'Cel puțin 6 caractere' : 'At least 6 characters'}>
            <PasswordInput show={showPassword} onToggle={() => setShowPassword(!showPassword)} ro={ro} autoComplete="new-password" value={form.managerPassword} disabled={loading} onChange={set('managerPassword')} />
          </Field>
          <Field label={ro ? 'Repetă parola' : 'Repeat password'}>
            <input type={showPassword ? 'text' : 'password'} autoComplete="new-password" value={form.confirmPassword} disabled={loading} onChange={set('confirmPassword')} />
          </Field>
          <Field label={ro ? 'Localitate (opțional)' : 'Location (optional)'}><input value={form.location} disabled={loading} onChange={set('location')} /></Field>
          <label className="au-check">
            <input type="checkbox" checked={form.acceptTerms} disabled={loading} onChange={set('acceptTerms')} />
            {ro ? 'Accept termenii și condițiile și politica de confidențialitate' : 'I accept the terms and conditions and the privacy policy'}
          </label>
          <button type="submit" className="pm-btn-primary" disabled={loading}>{loading ? (ro ? 'Se creează…' : 'Creating…') : (ro ? 'Creează ferma' : 'Create farm')}</button>
        </form>
      )}
      {backLink}
    </AuthLayout>
  );
};

export default FirebaseFarmRegistration;
